import { useScrollFadeIn } from '../../../hooks/useScrollFadeIn';

const variants = [
  {
    id: 'black',
    label: 'Black',
    labelJa: 'ブラック',
    desc: '洗練されたオールブラックの存在感。どんな空間にも馴染み、プロフェッショナルな印象を与えます。',
    tag: '定番人気',
    image: 'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/ee7a13827c35706a1f60f43108fa1054.jpeg',
    bg: 'bg-[#f5f5f5]',
    accent: 'bg-gray-900',
    accentText: 'text-white',
    tagBg: 'bg-gray-900 text-white',
    url: 'https://mever.design/products/mever-chair-pro-ver2?variant=51384900911394',
  },
  {
    id: 'gray',
    label: 'Gray',
    labelJa: 'グレー',
    desc: '上品なグレートーンが空間に柔らかさをプラス。クリエイティブな環境にも自然に溶け込みます。',
    tag: 'ミニマル',
    image: 'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/e66f4348b80bdd08ca0ca67ae46e7a48.jpeg',
    bg: 'bg-[#efefed]',
    accent: 'bg-[#888880]',
    accentText: 'text-white',
    tagBg: 'bg-[#888880] text-white',
    url: 'https://mever.design/products/mever-chair-pro-ver2?variant=51384900878626',
  },
];

export default function ColorVariants() {
  const { ref, isVisible } = useScrollFadeIn({ delay: 150 });

  return (
    <section
      ref={ref}
      className={`py-20 md:py-40 px-5 md:px-10 bg-white transition-all duration-1000 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      {/* ヘッダー */}
      <div className="text-center mb-12 md:mb-24">
        <p className="text-[10px] tracking-[0.35em] uppercase text-gray-400 font-semibold mb-5">
          Color Variants
        </p>
        <h2 className="text-3xl md:text-5xl font-black text-gray-900 tracking-tight leading-tight mb-5">
          2色展開。各色50脚限定。
        </h2>
        <p className="text-sm md:text-base text-gray-500 max-w-xl mx-auto leading-relaxed">
          あなたの空間と個性に合わせて選べる、2つのカラーバリエーション。
        </p>
      </div>

      {/* 左右シンメトリーレイアウト */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {variants.map((v) => (
          <div
            key={v.id}
            className={`relative rounded-3xl overflow-hidden ${v.bg} group cursor-pointer`}
            style={{ transition: 'box-shadow 0.5s ease, transform 0.5s ease' }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-6px) scale(1.015)';
              (e.currentTarget as HTMLDivElement).style.boxShadow = '0 32px 64px rgba(0,0,0,0.13)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0) scale(1)';
              (e.currentTarget as HTMLDivElement).style.boxShadow = '0 0px 0px rgba(0,0,0,0)';
            }}
          >
            {/* タグ */}
            <div className="absolute top-6 left-6 z-10">
              <span className={`text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full ${v.tagBg}`}>
                {v.tag}
              </span>
            </div>

            {/* 画像エリア */}
            <div className="w-full h-[360px] md:h-[520px] flex items-center justify-center overflow-hidden relative">
              <img
                src={v.image}
                alt={`MeVER Chair Pro ${v.labelJa}`}
                className="w-full h-full object-contain object-center transition-transform duration-700 ease-out group-hover:scale-110"
              />
              {/* ホバー時グラデーションオーバーレイ */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>

            {/* テキストエリア */}
            <div className="px-6 md:px-8 pb-8 md:pb-10 pt-2">
              <p className="text-[10px] tracking-[0.3em] uppercase text-gray-400 font-medium mb-2 transition-all duration-500 group-hover:tracking-[0.45em]">
                {v.label}
              </p>
              <h3 className="text-xl md:text-2xl font-black text-gray-900 mb-3 tracking-tight transition-all duration-300 group-hover:translate-x-1">
                {v.labelJa}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-5 md:mb-6 transition-all duration-300 group-hover:text-gray-700">
                {v.desc}
              </p>
              <a
                href={v.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-full md:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full text-sm font-bold transition-all duration-300 hover:opacity-90 hover:scale-[1.05] hover:shadow-lg active:scale-[0.97] whitespace-nowrap cursor-pointer ${v.accent} ${v.accentText}`}
                style={{ transition: 'transform 0.3s ease, box-shadow 0.3s ease, opacity 0.3s ease' }}
              >
                このカラーを予約する
                <i className="ri-arrow-right-line text-base transition-transform duration-300 group-hover:translate-x-1"></i>
              </a>
            </div>

            {/* ホバー時ボーダーグロー */}
            <div className="absolute inset-0 rounded-3xl ring-0 group-hover:ring-2 group-hover:ring-gray-300/60 transition-all duration-500 pointer-events-none" />
          </div>
        ))}
      </div>

      {/* 限定数バナー */}
      <div className="max-w-6xl mx-auto mt-8 md:mt-10">
        <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap text-xs md:text-sm text-gray-400 px-4">
          <i className="ri-time-line text-base"></i>
          <span>先行予約受付中</span>
          <span className="text-gray-300">·</span>
          <span className="font-semibold text-gray-700">各色50脚限定</span>
          <span className="text-gray-300">·</span>
          <span>なくなり次第終了</span>
        </div>
      </div>
    </section>
  );
}